import { useEffect, useRef, useState } from "react";
import type { RemoteSupportConfig } from "../scenario/types";
import { audio } from "../lib/audio";
import { activeTtsLang } from "../lib/locale";
import { tx, txr } from "../lib/tx";

type Stage =
  | "connecting"
  | "connected"
  | "takeover"
  | "opening"
  | "reading"
  | "exfil"
  | "done";

/**
 * Episode 13 — the remote-desktop takeover. Reached only when the learner
 * opened the remote-support tool during the live vishing call. An autonomous
 * cursor moves across a mock desktop, opens a confidential file, and copies it
 * out — the "one click handed them everything" moment made concrete. Non-
 * interactive by design: the learner watches their own machine act on its own,
 * then continues to the consequence.
 */
export function RemoteSupportOverlay({
  config,
  onContinue,
}: {
  config: RemoteSupportConfig;
  onContinue: () => void;
}) {
  const [stage, setStage] = useState<Stage>("connecting");
  // Cursor position as viewport % of the mock screen.
  const [cursor, setCursor] = useState({ x: 50, y: 88 });
  const [progress, setProgress] = useState(0);
  const timers = useRef<number[]>([]);

  const toolName = config.toolName;
  const fileName = config.fileName ?? "Project Horizon — Board Pack.docx";
  const partnerId = config.partnerId ?? "884 512 003";

  useEffect(() => {
    const push = (fn: () => void, ms: number) => {
      timers.current.push(window.setTimeout(fn, ms));
    };
    push(() => setStage("connected"), 1100);
    push(() => {
      setStage("takeover");
      audio.speak("Perfect, I can see your screen now. Just leave everything to me.", {
        lang: activeTtsLang(),
        voice: "onyx",
      });
    }, 2100);
    // Autonomous cursor path: drift to the file icon.
    push(() => setCursor({ x: 22, y: 34 }), 2900);
    push(() => setCursor({ x: 21, y: 33 }), 3700);
    push(() => setStage("opening"), 4100);
    push(() => setStage("reading"), 5200);
    // Drift to a "download / copy" control.
    push(() => setCursor({ x: 78, y: 20 }), 6300);
    push(() => setStage("exfil"), 6900);
    return () => {
      timers.current.forEach((t) => window.clearTimeout(t));
      audio.cancelSpeech();
    };
  }, []);

  // Exfil progress bar.
  useEffect(() => {
    if (stage !== "exfil") return;
    const iv = window.setInterval(() => {
      setProgress((p) => {
        const next = Math.min(100, p + 7);
        if (next >= 100) {
          window.clearInterval(iv);
          window.setTimeout(() => setStage("done"), 500);
        }
        return next;
      });
    }, 160);
    return () => window.clearInterval(iv);
  }, [stage]);

  const takeoverActive = stage === "takeover" || stage === "opening" || stage === "reading" || stage === "exfil" || stage === "done";
  const fileOpen = stage === "reading" || stage === "exfil" || stage === "done";

  return (
    <div className="rso-layer">
      <div className="rso-frame">
        {/* Remote-session banner */}
        <div className="rso-banner">
          <span className="rso-dot" />
          {txr("{toolName}: you are sharing your screen with partner <0>{partnerId}</0>", { toolName, partnerId }, [<b />])}
          {takeoverActive && <span className="rso-remote-tag">{tx("remote control active")}</span>}
        </div>

        {/* Mock desktop */}
        <div className="rso-desktop">
          <div className="rso-icons">
            <div className={`rso-icon ${fileOpen ? "sel" : ""}`}>
              <span className="rso-icon-img">{"\u{1F4C4}"}</span>
              <span className="rso-icon-lbl">{fileName}</span>
            </div>
            <div className="rso-icon">
              <span className="rso-icon-img">{"\u{1F4C1}"}</span>
              <span className="rso-icon-lbl">{tx("Finance")}</span>
            </div>
            <div className="rso-icon">
              <span className="rso-icon-img">{"\u{1F4E7}"}</span>
              <span className="rso-icon-lbl">{tx("Mail")}</span>
            </div>
          </div>

          {fileOpen && (
            <div className="rso-window">
              <div className="rso-window-bar">
                <span>{fileName}</span>
                <span className="rso-window-x">×</span>
              </div>
              <div className="rso-window-body">
                <div className="rso-doc-h">{tx("CONFIDENTIAL — Project Horizon")}</div>
                <div className="rso-doc-l" />
                <div className="rso-doc-l" />
                <div className="rso-doc-l short" />
                <div className="rso-doc-l" />
                <div className="rso-doc-l short" />
              </div>
            </div>
          )}

          {stage === "exfil" && (
            <div className="rso-exfil">
              <div className="rso-exfil-txt">{tx("Copying files to remote host…")}</div>
              <div className="rso-exfil-bar">
                <div className="rso-exfil-fill" style={{ width: `${progress}%` }} />
              </div>
              <div className="rso-exfil-sub">{fileName}</div>
            </div>
          )}

          {/* Autonomous cursor */}
          <div
            className={`rso-cursor ${takeoverActive ? "auto" : ""}`}
            style={{ left: `${cursor.x}%`, top: `${cursor.y}%` }}
            aria-hidden
          >
            {"\u25B2"}
          </div>

          {stage === "connecting" && <div className="rso-status">{tx("Establishing remote session…")}</div>}
          {stage === "connected" && <div className="rso-status">{tx("Connected. Handing over control…")}</div>}
        </div>

        {stage === "done" && (
          <div className="rso-done">
            <div className="rso-done-title">{tx("They're in.")}</div>
            <p>
              {txr("The moment you opened {toolName}, the caller took control of your machine — and copied a confidential Project Horizon file straight off your desktop. You never typed a password.", { toolName })}
            </p>
            <button className="rso-continue" onClick={onContinue}>
              {config.continueLabel ?? tx("See what just happened")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
